// Vista previa en pantalla del Control manual de cédulas — mismo patrón que
// el ERP (FacturaPreviewModal): barra de acciones arriba (Imprimir / Enviar
// por WhatsApp / Cerrar) y el documento visible debajo, en vez de mandarlo
// directo a un diálogo de impresión oculto.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Printer, MessageCircle, X, Loader2 } from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { inputCls } from '../../components/ui';
import { buildControlCedulasHtml, type BoletaControlCedulas, type FiltrosControlCedulas } from './controlCedulasImprimir';

interface Props {
  boletas: BoletaControlCedulas[];
  filtros: FiltrosControlCedulas;
  empresaNombre: string;
  empresaCedula: string;
  empresaLogo: string;
  empresaId: number;
  onClose: () => void;
}

export function ControlCedulasPreview({ boletas, filtros, empresaNombre, empresaCedula, empresaLogo, empresaId, onClose }: Props) {
  const html = useMemo(
    () => buildControlCedulasHtml(boletas, filtros, empresaNombre, empresaCedula, empresaLogo),
    [boletas, filtros, empresaNombre, empresaCedula, empresaLogo],
  );
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [numero, setNumero] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  function imprimir() {
    iframeRef.current?.contentWindow?.focus();
    iframeRef.current?.contentWindow?.print();
  }

  async function enviarWhatsApp() {
    const num = numero.replace(/\D/g, '');
    if (num.length < 8) { setMsg({ tipo: 'err', texto: 'Ingresá un número válido.' }); return; }
    setEnviando(true); setMsg(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(buildApiUrl('/aplicaciones/reportes/enviar-whatsapp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          reporte_html: html,
          numero: num,
          titulo: 'control-cedulas',
          caption: `Control manual de cédulas — ${boletas.length} pendiente${boletas.length === 1 ? '' : 's'}`,
          landscape: true,
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || 'No se pudo enviar');
      setMsg({ tipo: 'ok', texto: `Enviado a ${num}.` });
    } catch (e: any) {
      setMsg({ tipo: 'err', texto: e?.message || 'Error al enviar' });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#e5e7eb', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        background: '#0f172a', borderBottom: '1px solid #1e3a5f', padding: '10px 16px',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={imprimir} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid rgba(59,130,246,.5)', background: 'rgba(30,58,138,.3)', color: '#93c5fd', padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', borderRadius: 6 }}>
            <Printer size={15} /> Imprimir
          </button>
          <input
            value={numero}
            onChange={e => setNumero(e.target.value)}
            placeholder="Número WhatsApp"
            className={inputCls}
            style={{ width: 160, background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.2)' }}
          />
          <button onClick={enviarWhatsApp} disabled={enviando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid rgba(34,197,94,.4)', background: 'rgba(20,83,45,.3)', color: '#4ade80', padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer', opacity: enviando ? 0.6 : 1, borderRadius: 6 }}>
            {enviando ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
            {enviando ? 'Enviando…' : 'Enviar por WhatsApp'}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.tipo === 'ok' ? '#4ade80' : '#fca5a5' }}>{msg.tipo === 'ok' ? '✓ ' : ''}{msg.texto}</span>}
        </div>
        <button onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid #334155', background: 'transparent', color: '#94a3b8', padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', borderRadius: 6 }}>
          <X size={14} /> Cerrar (Esc)
        </button>
      </div>
      <iframe
        ref={iframeRef}
        title="Control manual de cédulas"
        srcDoc={html}
        style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
      />
    </div>
  );
}
